import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { constants, colors } from "utils";
import { trackClickedButton } from "utils/analytics";
import { logError, LogCategory } from "utils/sentryLogger";
import { buildingTowardOrTier1, sortPrerequisites, sortStandardsByCode } from "utils/standards";
import {
  useClassroomLessonAssignment,
  patchGroupAssignments,
} from "hooks/useCurriculum/useCurriculum";
import { QuestionFilterView } from "components/pages/LessonsStandards/QuestionFilterView";
import { EMPTY_QUESTION_FILTER, serializeQuestionFilter, fromQuestionFilter } from "components/pages/LessonsStandards/questionFilters";
import { PREREQ_SECTIONS } from "components/shared/PrerequisiteTierSections";
import { StandardPicker, AccordionSection, StandardRadioRow } from "./StandardPicker";
import { StandardLibrarySection } from "./StandardLibrarySection";

// Lesson track enum is singular "extension"; the picker's trackMeta keys are
// plural. Map for display only — the raw enum is preserved on the wire.
const toDisplayTrack = (t) => (t === "extension" ? "extensions" : t);

// Left padding matches the rows' pl=36 so tier headers align with their options.
const TierLabel = styled.div`
  color: ${colors.nightlyWoods500};
  font-family: "ABC Marfa", sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  padding: 12px 8px 4px 36px;
`;

// The core / prerequisite / extension options come from the lesson's own
// alignments (the backend constrains those tracks to the lesson's set). Any
// other standard can still be assigned via the "other" track through the
// Standards Library search/browse section below.
const buildOptions = (alignments, trackType) =>
  (alignments || []).map((a) => ({
    code: a.standard_code,
    name: a.standard_name,
    description: a.standard_description,
    trackType,
  }));

// The curriculum group-assignments PATCH is a DELTA: send only the single changed
// code. track_type is passed through as-is (core / prerequisites / extension /
// other) — extensionOptions are already built with singular "extension", so no
// toLessonTrack mapping here. `questionFilter` (optional, snake_case) carries the
// teacher's Advanced Selection onto the code; omitted when there's no active filter.
export const buildAssignmentDelta = (groupCode, selected, questionFilter) => [
  {
    group_code: groupCode,
    track_type: selected.trackType,
    standard_code: selected.code,
    ...(questionFilter ? { question_filter: questionFilter } : {}),
  },
];

export const LessonEditGroupFocus = () => {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const classroomId = params.get("classroomId");
  const groupCode = params.get("groupCode");
  const lessonIdParam = params.get("lessonId");
  const assignmentIdParam = params.get("assignmentId");
  const classId = params.get("classId");

  const { assignment, loading } = useClassroomLessonAssignment(classroomId);
  // The classroom carries a collection of lessons; pick the bundle this page is
  // editing — by the assignmentId in the URL, else the lessonId, else the lesson
  // that owns this group code, else the first assigned lesson. assignmentId wins
  // because the same lesson can run in more than one bundle, and matching on
  // lessonId then resolves to whichever bundle happens to come first.
  const lessonEntry = useMemo(() => {
    const lessons = assignment?.lessons || [];
    const byAssignment = assignmentIdParam && lessons.find((l) => l.assignment_id === assignmentIdParam);
    if (byAssignment) return byAssignment;
    const byId = lessonIdParam && lessons.find((l) => l.lesson?.id === lessonIdParam);
    if (byId) return byId;
    const byCode = lessons.find((l) => (l.assignments || []).some((a) => a.group_code === groupCode));
    return byCode || lessons[0] || null;
  }, [assignment, assignmentIdParam, lessonIdParam, groupCode]);
  const lesson = lessonEntry?.lesson || null;
  const assignments = useMemo(() => lessonEntry?.assignments || [], [lessonEntry]);
  const lessonId = lessonIdParam || lesson?.id || null;

  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Which option's inline questions+filters are open (one at a time).
  const [expandedKey, setExpandedKey] = useState(null);
  // Per-option filter state, keyed `${trackType}|${code ?? ""}`. Core keys on "" since
  // its filter is track-wide (standard_code null on the wire). Reset when the lesson
  // this page edits changes.
  const [filters, setFilters] = useState({});
  useEffect(() => { setFilters({}); setExpandedKey(null); }, [lessonId]);
  const optionFilterKey = (opt) => `${opt.trackType}|${opt.trackType === "core" ? "" : (opt.code || "")}`;
  const getOptionFilter = (opt) => filters[optionFilterKey(opt)] || EMPTY_QUESTION_FILTER;
  const setOptionFilter = (opt, next) => setFilters((prev) => ({ ...prev, [optionFilterKey(opt)]: next }));

  const goBackToGroups = useCallback(() => {
    const path = constants.EDUCATOR_PATH;
    history.push(classId ? `${path}?classId=${classId}` : path);
  }, [history, classId]);

  // Core is the lesson's whole set: a single option addressing every standard in
  // `addressing`, not one selectable row per standard. `code` is the primary
  // (addressing[0]) — what the save sends and what the current assignment points
  // at; `codes` is the full set for display. Prereq/extension stay per-standard.
  const coreOption = useMemo(() => {
    const addressing = lesson?.addressing || [];
    if (!lesson || addressing.length === 0) return null;
    return {
      code: addressing[0].standard_code,
      codes: addressing.map((a) => a.standard_code),
      name: lesson.name,
      description: lesson.learning_goals || lesson.student_facing_goal || "",
      trackType: "core",
    };
  }, [lesson]);
  // Prereq options grouped by tier when the lesson carries prerequisite_tiers
  // (tier standards are code/name/description in leaf form — already valid PATCH
  // targets). The flat building_on list remains the fallback so nothing changes
  // until the backend serves tiers.
  const prereqTierGroups = useMemo(() => {
    const tiers = (lesson?.prerequisite_tiers || []).filter((g) => g.standards?.length);
    if (tiers.length === 0) {
      // Sorted after buildOptions — the raw alignments key on standard_code, not code.
      const options = sortPrerequisites(buildOptions(lesson?.building_on, "prerequisites"));
      return options.length ? [{ tier: null, options }] : [];
    }
    return PREREQ_SECTIONS.flatMap(({ level, tier }) => {
      const grp = tiers.find((g) => g.level === level);
      if (!grp) return [];
      return [{
        tier,
        options: sortPrerequisites(grp.standards.map((s) => ({ ...s, trackType: "prerequisites" }))),
      }];
    });
  }, [lesson]);
  const prereqOptions = useMemo(() => prereqTierGroups.flatMap((g) => g.options), [prereqTierGroups]);
  const extensionOptions = useMemo(
    () => sortStandardsByCode(buildOptions(buildingTowardOrTier1(lesson), "extension")),
    [lesson],
  );
  const allOptions = useMemo(
    () => [...(coreOption ? [coreOption] : []), ...prereqOptions, ...extensionOptions],
    [coreOption, prereqOptions, extensionOptions],
  );

  // "Grade 3, Unit 2, Lesson 11 Area and the Multiplication Table" — the lesson's
  // course / unit / lesson ordinals plus its name, per the Figma core header.
  const lessonLabel = useMemo(() => {
    if (!lesson) return "";
    const ordinals = [lesson.course_name, lesson.unit_ordinal_name, lesson.ordinal_name]
      .filter(Boolean)
      .join(", ");
    return [ordinals, lesson.name].filter(Boolean).join(" ");
  }, [lesson]);

  // The raw assignment for this code (lesson enum track), joined to its name.
  const rawCard = useMemo(
    () => assignments.find((a) => a.group_code === groupCode) || null,
    [assignments, groupCode],
  );
  const currentCard = useMemo(() => {
    if (!rawCard?.standard_code) return null;
    // Core selection reflects the whole set + the lesson name, matching the card.
    if (rawCard.track_type === "core" && coreOption) {
      const codes = rawCard.standard_codes?.length ? rawCard.standard_codes : coreOption.codes;
      return {
        standard_code: codes.join(", "),
        standard_name: coreOption.name,
        track_type: "core",
      };
    }
    const match = allOptions.find((o) => o.code === rawCard.standard_code);
    return {
      standard_code: rawCard.standard_code,
      standard_name: match?.name || rawCard.standard_code,
      track_type: toDisplayTrack(rawCard.track_type),
    };
  }, [rawCard, allOptions, coreOption]);

  // Preselect the option matching the code's current standard. A code currently
  // on the "other" track points at a library standard outside the lesson's
  // alignments, so seed the selection directly from the card.
  useEffect(() => {
    if (selected || !rawCard?.standard_code) return;
    // A core code selects the whole-set core option regardless of which primary
    // standard it points at.
    let option = null;
    if (rawCard.track_type === "core" && coreOption) {
      option = coreOption;
    } else {
      const match = allOptions.find((o) => o.code === rawCard.standard_code);
      if (match) option = match;
      else if (rawCard.track_type === "other") {
        option = {
          code: rawCard.standard_code,
          name: currentCard?.standard_name || rawCard.standard_code,
          description: "",
          trackType: "other",
        };
      }
    }
    if (!option) return;
    setSelected(option);
    // Hydrate the group's stored filter so it shows on load (and survives re-save).
    const hydrated = fromQuestionFilter(rawCard.question_filter);
    if (hydrated) setFilters((prev) => ({ ...prev, [optionFilterKey(option)]: hydrated }));
  }, [allOptions, rawCard, currentCard, selected, coreOption]);

  // Enable Save when the standard changed OR the filter differs from what's stored.
  // A standard change always counts (and clears the filter BE-side); the filter is
  // compared against the hydrated stored value so merely re-opening isn't a change.
  const standardChanged = Boolean(selected && selected.code !== rawCard?.standard_code);
  const selectedQuestionFilter = selected ? serializeQuestionFilter(getOptionFilter(selected)) : null;
  const storedQuestionFilter = serializeQuestionFilter(fromQuestionFilter(rawCard?.question_filter) || EMPTY_QUESTION_FILTER);
  const filterChanged = !standardChanged && JSON.stringify(selectedQuestionFilter) !== JSON.stringify(storedQuestionFilter);
  const hasChanged = Boolean(standardChanged || filterChanged);

  const onAssign = async () => {
    if (!hasChanged || saving || !classroomId || !groupCode || !lessonEntry?.assignment_id) return;
    setSaving(true);
    setError(null);
    try {
      await patchGroupAssignments(
        classroomId,
        lessonEntry.assignment_id,
        buildAssignmentDelta(groupCode, selected, selectedQuestionFilter),
      );
      trackClickedButton("Assign Group Focus", {
        page_name: "Edit Group Focus",
        assignment_type: "Lesson",
        class: classroomId || null,
        lesson_id: lessonId || null,
        group_code: groupCode,
        standard_code: selected.code,
        track_type: selected.trackType,
      });
      goBackToGroups();
    } catch (e) {
      setError(e?.detail || e?.message || "Failed to assign standard.");
      logError("LessonEditGroupFocus: failed to assign", e, {
        category: LogCategory.ERROR,
        classroomId,
        lessonId,
        groupCode,
        standardCode: selected?.code,
      });
    } finally {
      setSaving(false);
    }
  };

  const renderRow = (option, pl) => {
    const rowKey = `${option.trackType}:${option.code}`;
    const isCore = option.trackType === "core";
    return (
      <StandardRadioRow
        key={rowKey}
        option={option}
        pl={pl}
        checked={selected?.code === option.code && selected?.trackType === option.trackType}
        onSelect={setSelected}
        expanded={expandedKey === rowKey}
        onToggleView={() => setExpandedKey((k) => (k === rowKey ? null : rowKey))}
        expandedContent={(
          <QuestionFilterView
            lessonId={lessonId}
            standardCode={isCore ? null : option.code}
            filter={getOptionFilter(option)}
            onFilterChange={(next) => setOptionFilter(option, next)}
            showStandards={isCore}
            showAutoAssign
            background={colors.milkGlass200}
          />
        )}
      />
    );
  };

  const primaryCode = coreOption?.code || "";

  // Open the section holding the group's CURRENT focus on load (core falls back
  // as the default for unassigned codes) — sections mount only after the
  // assignment has loaded (StandardPicker gates children on isLoading), so the
  // track is always known when defaultExpanded is captured.
  const currentTrack = rawCard?.track_type || "core";

  return (
    <StandardPicker
      groupCode={groupCode}
      currentCard={currentCard}
      isLoading={loading && !assignment}
      saving={saving}
      canAssign={Boolean(hasChanged)}
      onAssign={onAssign}
      onCancel={goBackToGroups}
      error={error}
      onClearError={() => setError(null)}
    >
      {coreOption && (
        <AccordionSection title={`Core${lessonLabel ? `: ${lessonLabel}` : ""}`} defaultExpanded={currentTrack === "core"}>
          {renderRow(coreOption, 36)}
        </AccordionSection>
      )}
      {prereqOptions.length > 0 && (
        <AccordionSection title="Prerequisites" defaultExpanded={currentTrack === "prerequisites"}>
          {prereqTierGroups.map((g) => (
            <React.Fragment key={g.tier || "flat"}>
              {g.tier && <TierLabel>{g.tier}</TierLabel>}
              {g.options.map((o) => renderRow(o, 36))}
            </React.Fragment>
          ))}
        </AccordionSection>
      )}
      {extensionOptions.length > 0 && (
        <AccordionSection title="Extensions" defaultExpanded={currentTrack === "extension"}>
          {extensionOptions.map((o) => renderRow(o, 36))}
        </AccordionSection>
      )}

      <StandardLibrarySection
        primaryCode={primaryCode}
        selected={selected}
        onSelect={setSelected}
        defaultExpanded={currentTrack === "other"}
      />
    </StandardPicker>
  );
};
